import type { FC } from 'react';
import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import ErrorRefreshIcon from '@public/assets/casino/icons/errorRefresh.svg';
import VpnErrorIcon from '@public/assets/casino/icons/vpnError.svg';
import InfoCircleIcon from '@public/assets/common/icons/infoCircle.svg';
import { ModalMessage } from '@/components/ModalMessage/ModalMessage';
import { routes } from '@/constants';
import { useGameGoBack } from '../../CasinoGamePage.hooks';
import { useGameModal } from './CasinoGameModals.hooks';
import type { CasinoGameModalsProps } from './CasinoGameModals.types';
import styles from './CasinoGameModals.module.scss';
const popupClasses = {
  wrapper: styles.wrapper
};
export const CasinoGameModals: FC<CasinoGameModalsProps> = ({
  urlError,
  mode,
  game,
  refreshRequest
}) => {
  const {
    t
  } = useTranslation('casino');
  const router = useRouter();
  const back = useGameGoBack(game);
  const isRestrictedError = urlError?.reason === 'RESTRICTED_COUNTRY';
  const isInternalError = urlError?.code?.toUpperCase() === 'INTERNAL';
  const isOtherError = Boolean(urlError) && !isRestrictedError && !isInternalError;
  const {
    show: restrictedModalShow,
    onClose: restrictedModalOnClose
  } = useGameModal(isRestrictedError);
  const {
    show: refreshModalShow,
    onClose: refreshModalOnClose
  } = useGameModal(isOtherError);
  const {
    show: internalModalShow,
    onClose: internalModalOnClose
  } = useGameModal(isInternalError);
  const {
    show: disabledDemoModalShow,
    onClose: disabledDemoModalOnClose
  } = useGameModal(mode === 'DEMO' && game && !game.hasDemoMode);
  const handleRefresh = useCallback(() => {
    refreshRequest();
    refreshModalOnClose();
  }, [refreshRequest, refreshModalOnClose]);
  const handleCloseRefreshModal = useCallback(() => {
    back();
    refreshModalOnClose();
  }, [back, refreshModalOnClose]);
  const handleClickReplace = useCallback(() => {
    disabledDemoModalOnClose();
    router.replace(routes.home);
  }, [router, disabledDemoModalOnClose]);
  const onCloseRestrictedModal = useCallback(() => {
    restrictedModalOnClose();
    back();
  }, [back, restrictedModalOnClose]);
  const handleClose = () => {
    internalModalOnClose();
    router.push(routes.casino.home);
  };
  return <>
      <ModalMessage show={disabledDemoModalShow} onClose={handleClickReplace} onClick={handleClickReplace} title={t('warning.text', 'Warning', {
      ns: 'common'
    })} content={t('game.demo_mode_disable', 'Game does not has a demo mode')} icon={<InfoCircleIcon className={styles.icon} />} popupClasses={popupClasses} buttonProps={{
      as: 'button',
      color: 'grey'
    }} data-sentry-element="ModalMessage" data-sentry-source-file="CasinoGameModals.tsx" />
      <ModalMessage show={internalModalShow} onClose={handleClose} status="error" buttonText={t('game.load.error_internal.button', 'Ok')} icon={<ErrorRefreshIcon className={styles.icon} />} onClick={handleClose} title={t('game.load.error_internal.message', 'Game is not available')} popupClasses={popupClasses} data-sentry-element="ModalMessage" data-sentry-source-file="CasinoGameModals.tsx" />
      <ModalMessage show={refreshModalShow} onClose={handleCloseRefreshModal} status="error" buttonText={t('game.load.error.button', 'Refresh')} icon={<ErrorRefreshIcon className={styles.icon} />} onClick={handleRefresh} title={t('game.load.error.message', 'Game loading error')} popupClasses={popupClasses} data-sentry-element="ModalMessage" data-sentry-source-file="CasinoGameModals.tsx" />
      <ModalMessage withCloseButton={false} show={restrictedModalShow} onClose={onCloseRestrictedModal} buttonText={t('game.load.error_restricted.button', 'Close')} content={t('game.load.error_restricted.message', 'The game is not available for your country. If you use VPN services, then switch to another country.')} onClick={onCloseRestrictedModal} icon={<VpnErrorIcon className={styles.icon} />} popupClasses={popupClasses} data-sentry-element="ModalMessage" data-sentry-source-file="CasinoGameModals.tsx" />
    </>;
};